import React, { useEffect, useState } from "react";
import ControlDetailsPanel from "./ControlDetailsPanel";
import { IControlLedStripe, TLedMode } from "../types";
import {
  TUpdateAttributePayload,
  useSocketClient,
} from "../../../contexts/SocketClientProvider";
import Select, { TOption } from "../../../Components/Select";
import LabeledPerCentSlider from "../../../Components/PerCentSlider";
import { useTranslation } from "react-i18next";

const DefaultDecay: number = 40;
const DefaultGain: number = 40;

type TSettings = {
  m: number;
  b: number;
  d?: number;
  g?: number;
};

interface IControlDetailsLedStripeProps {
  control: IControlLedStripe;
}

const ControlDetailsLedStripe = (props: IControlDetailsLedStripeProps) => {
  const { control } = props;
  const [available, setAvailable] = useState<boolean>(
    control.attributes.available === "yes"
  );
  const [modes, setModes] = useState<TLedMode[]>(
    control.attributes.modes || []
  );
  const [currentMode, setCurrentMode] = useState<number>(
    control.attributes.current_mode || 0
  );
  const [, setModeName] = useState<string>(modes[currentMode]?.name || "");
  const [brightness, setBrightness] = useState<number>(
    modes[currentMode]?.brightness
  );
  const [decay, setDecay] = useState<number>(
    modes[currentMode]?.decay || DefaultDecay
  );
  const [gain, setGain] = useState<number>(
    modes[currentMode]?.gain || DefaultGain
  );

  const socketClient = useSocketClient();
  const { t } = useTranslation();

  useEffect(() => {
    const callback = (payload: TUpdateAttributePayload) => {
      if (payload.id === control.id) {
        if (payload.attribute === "available") {
          setAvailable(payload.value === "yes");
        } else if (payload.attribute === "modes") {
          setModes(payload.value);
        } else if (payload.attribute === "current_mode") {
          setCurrentMode(payload.value);
          setModeName(modes[currentMode].name);
          setBrightness(modes[currentMode].brightness);
          setDecay(modes[currentMode].decay || DefaultDecay);
          setGain(modes[currentMode].gain || DefaultGain);
        } else {
          console.log(
            "[ControlLedStripe] UNHANDLED ATTRIBUTE ",
            payload.attribute,
            payload.value
          );
        }
      }
    };
    socketClient.subscribe(callback, "update_attribute");
    return () => socketClient.unsubscribe(callback, "update_attribute");
  }, [control.id, currentMode, modes, socketClient]);

  const updateSettings = (settings: TSettings) => {
    if (modes[settings.m].name !== "SoundReactive") {
      delete settings.d;
      delete settings.g;
    }
    socketClient.process_command({
      type: "control",
      id: control.id,
      command: "set_settings",
      value: settings,
    });
  };

  const changeCurrentMode = (newCurrentMode: number) => {
    setCurrentMode(newCurrentMode);
    const newBrightness = modes[newCurrentMode].brightness;
    setBrightness(modes[newCurrentMode].brightness);
    const newDecay = modes[newCurrentMode].decay || DefaultDecay;
    setDecay(newDecay);
    const newGain = modes[newCurrentMode].gain || DefaultGain;
    setGain(newGain);
    updateSettings({
      m: newCurrentMode,
      b: newBrightness,
      d: newDecay,
      g: newGain,
    });
  };

  const updateCurrentMode = (
    value: { brightness: number } | { decay: number } | { gain: number }
  ) => {
    const newModes = modes.map(function (mode, index): TLedMode {
      return index === currentMode ? { ...mode, ...value } : mode;
    });
    setModes(newModes);
  };

  const changeBrightness = (newBrightness: number) => {
    setBrightness(newBrightness);
    updateCurrentMode({ brightness: newBrightness });
    updateSettings({
      m: currentMode,
      b: newBrightness,
      d: decay,
      g: gain,
    });
  };

  const changeDecay = (newDecay: number) => {
    setDecay(newDecay);
    updateCurrentMode({ decay: newDecay });
    updateSettings({
      m: currentMode,
      b: brightness,
      d: newDecay,
      g: gain,
    });
  };

  const changeGain = (newGain: number) => {
    setGain(newGain);
    updateCurrentMode({ gain: newGain });
    updateSettings({
      m: currentMode,
      b: brightness,
      d: decay,
      g: newGain,
    });
  };

  const modeOptions: TOption<number>[] = modes.map((mode, index) => {
    return { label: mode.name, value: index };
  });

  if (!available) {
    return <></>;
  }

  return (
    <ControlDetailsPanel
      mode={control.is_enabled ? "on" : "disabled"}
      title={control.name}
    >
      <Select
        fullWidth
        margin="none"
        label={t("Mode")}
        value={currentMode}
        options={modeOptions}
        onChange={(event) => {
          changeCurrentMode(parseInt(event.target.value));
        }}
      />
      <LabeledPerCentSlider
        label={t("Brightness")}
        value={brightness}
        handleChange={changeBrightness}
      />
      {modes[currentMode].name === "SoundReactive" && (
        <>
          <LabeledPerCentSlider
            label={t("Decay")}
            value={decay}
            handleChange={changeDecay}
          />
          <LabeledPerCentSlider
            label={t("Gain")}
            value={gain}
            handleChange={changeGain}
          />
        </>
      )}
    </ControlDetailsPanel>
  );
};

export default ControlDetailsLedStripe;
